import { useState } from 'react';
import { Button } from '@design/Button';
import { useEventBus } from '@bus/useEventBus';
import { clsx } from 'clsx';
import type { HintChain } from '@curriculum/types';

/**
 * Tutor panel — graduated-hint interaction.
 *
 * Strict policy (evidence: secondary-student survey n=330; 52.4% want graduated
 * hints, only 3.0% want the answer revealed, 54.9% explicitly reject
 * answer-giving): the tutor surfaces hints in three escalating levels per
 * misconception chain. Never reveals the answer. Each click emits a
 * concept.hint event so the future LLM tutor learns which misconceptions a
 * student is engaging with.
 *
 * Evidence: Aleven & Koedinger Help-Seeking Tutor; Salden et al. 2010.
 */
export function TutorPanel({ chapterId, hintChains }: { chapterId: string; hintChains: HintChain[] }) {
  const emit = useEventBus();
  const [openChain, setOpenChain] = useState<number | null>(null);
  const [hintLevel, setHintLevel] = useState<Record<number, number>>({}); // chainIdx -> hint level (1..3)

  const requestNextHint = (chainIdx: number) => {
    const next = (hintLevel[chainIdx] ?? 0) + 1;
    if (next > 3) return;
    setHintLevel({ ...hintLevel, [chainIdx]: next });
    emit({
      kind: 'concept.hint',
      payload: { conceptId: `${chapterId}:misconception:${chainIdx}`, attemptNumber: next },
    });
  };

  if (!hintChains.length) return null;

  return (
    <aside className="rounded-card-lg border border-ink-200/80 bg-cream shadow-card overflow-hidden">
      <header className="px-5 py-4 border-b border-ink-200/80 bg-gradient-to-b from-accent-50/70 to-cream">
        <div className="text-[11px] uppercase tracking-[0.14em] text-accent-600 font-semibold">Stuck?</div>
        <p className="text-ink-800 mt-1 text-sm leading-relaxed">Pick the thought closest to what's blocking you. I'll nudge — never spoil.</p>
      </header>

      <ul className="divide-y divide-ink-200">
        {hintChains.map((chain, idx) => {
          const level = hintLevel[idx] ?? 0;
          const isOpen = openChain === idx;
          return (
            <li key={idx} className="px-5 py-3.5">
              <button
                onClick={() => setOpenChain(isOpen ? null : idx)}
                className={clsx(
                  'w-full text-left flex items-start gap-2.5 text-sm leading-snug transition-colors duration-150',
                  isOpen ? 'text-ink-900' : 'text-ink-700 hover:text-ink-900',
                )}
              >
                <span className="font-mono text-accent-600 text-xs pt-0.5">{idx + 1}.</span>
                <span className="flex-1">"{chain.triggerMisconception}"</span>
                <span className="text-ink-500 text-sm font-medium">{isOpen ? '−' : '+'}</span>
              </button>

              {isOpen && (
                <div className="mt-3 ml-5">
                  {level === 0 ? (
                    <p className="text-sm text-ink-500 italic">Click the button for a small nudge. Three hints available — none reveal the answer.</p>
                  ) : (
                    <ol className="space-y-2">
                      {chain.hints.slice(0, level).map((h) => (
                        <li key={h.level} className="text-sm leading-relaxed bg-accent-50 border-l-2 border-accent-500 px-3 py-2.5 rounded-r-lg animate-pop-in motion-reduce:animate-none">
                          <span className="text-xs uppercase tracking-wide text-accent-600 font-semibold mr-2">Hint {h.level}</span>
                          {h.text}
                        </li>
                      ))}
                    </ol>
                  )}
                  {level < 3 && (
                    <Button variant="ghost" className="mt-2 !px-2 !py-1 text-xs" onClick={() => requestNextHint(idx)}>
                      {level === 0 ? 'Give me a nudge' : `Hint ${level + 1} →`}
                    </Button>
                  )}
                  {level === 3 && (
                    <p className="mt-2 text-xs text-ink-500 italic">That's all I'll give. The answer is yours to write — try the practice block.</p>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </aside>
  );
}
